import React from "react";
import toastr from "toastr";
import { KTMScreen, ActionEvent, RenderEvent, renderScreen } from "ktm-ui";
import { KTMApi } from "api/KTMApi";
import {
  Layout,
  Dialog,
  Button,
  Display,
  Line,
  Card,
  Column,
  Panel,
} from "ktm-ui";
import CustomToolTip from "../../components/CustomToolTip";

interface IdsBenefit {
  Id: string;
  Policy_Id: string;
  CommissionRatioOurPortion: number;
  CommissionRatioAdvisorPortion: number;
}

interface DefaultScenario {
  id: string;
  label: string | null;
  ourPortion: number;
  advisorPortion: number;
  notes: string | null;
}

interface DefaultsDistro {
  distroAccountId: string;
  distroName: string;
  scenarios: DefaultScenario[];
}

interface DefaultsResponse {
  distros: DefaultsDistro[];
}

interface PopupState {
  defaults: DefaultsResponse | null;
}

const KTM_COMMON_CALCULATIONS: Array<{
  adminFee: string;
  split: string;
  our: string;
  advisor: string;
}> = [
  { adminFee: "9.5%", split: "5/4.5", our: "53", advisor: "47" },
  { adminFee: "9%",   split: "6/3",   our: "67", advisor: "33" },
  { adminFee: "9%",   split: "5/4",   our: "56", advisor: "44" },
  { adminFee: "8.5%", split: "6/2.5", our: "71", advisor: "29" },
  { adminFee: "8%",   split: "6/2",   our: "75", advisor: "25" },
  { adminFee: "8%",   split: "5/3",   our: "63", advisor: "37" },
  { adminFee: "7%",   split: "6/1",   our: "86", advisor: "14" },
  { adminFee: "7%",   split: "5/2",   our: "71", advisor: "29" },
  { adminFee: "6%",   split: "5/1",   our: "83", advisor: "17" },
];

const KtmCommonCalculationsBody: React.FC = () => (
  <table style={{ fontSize: 12, borderCollapse: "collapse" }}>
    <thead>
      <tr style={{ borderBottom: "1px solid #ddd" }}>
        <th style={{ padding: "2px 8px", textAlign: "right" }}>Admin Fee</th>
        <th style={{ padding: "2px 8px", textAlign: "right" }}>Split</th>
        <th style={{ padding: "2px 8px", textAlign: "right" }}>Our %</th>
        <th style={{ padding: "2px 8px", textAlign: "right" }}>Advisor %</th>
      </tr>
    </thead>
    <tbody>
      {KTM_COMMON_CALCULATIONS.map((r, idx) => (
        <tr key={idx}>
          <td style={{ padding: "2px 8px", textAlign: "right" }}>{r.adminFee}</td>
          <td style={{ padding: "2px 8px", textAlign: "right" }}>{r.split}</td>
          <td style={{ padding: "2px 8px", textAlign: "right" }}>{r.our}</td>
          <td style={{ padding: "2px 8px", textAlign: "right" }}>{r.advisor}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default KTMScreen(
  { name: "enroll.product.edit-commission-ratio" },
  class extends React.Component<{ policyBenefit_id: string,  policy_id: string}, PopupState> {
    data: {
      dsBenefit: IdsBenefit;
    };

    state: PopupState = { defaults: null };

    // Store original data for comparison
    originalData: IdsBenefit | null = null;

    render() {
      return renderScreen(this);
    }

    async start({ screen }: ActionEvent) {
      screen.defaultRecord = "@dsBenefit";
      await KTMApi.start(screen, {
        "@policyBenefit_id": this.props.policyBenefit_id,
      });
      screen.title = "Edit Commission Ratio";

      // Store a copy of the original data
      this.originalData = { ...this.data.dsBenefit };

      // Kick off the defaults lookup but do NOT await it. Defaults are
      // advisory; the popup must paint without waiting on this round trip.
      fetch(
        `/api/Database/commission-ratio-defaults?policyBenefitId=${encodeURIComponent(this.props.policyBenefit_id)}`,
        { credentials: "include" }
      )
        .then((res) => (res.ok ? res.json() : null))
        .then((defaults: DefaultsResponse | null) => {
          if (defaults && defaults.distros && defaults.distros.length > 0) {
            this.setState({ defaults });
          }
        })
        .catch(() => {
          // Defaults are advisory; ignore lookup failures.
        });
    }

    main({ field, draw, action }: RenderEvent) {
      return (
        <Layout>
          {draw(this.header)}
          {draw(this.main_tab)}
          {draw(this.footer)}
        </Layout>
      );
    }

    header({ field, draw, action }: RenderEvent) {
      return <Dialog.Header label="Edit Commission Ratio" />;
    }

    splitSum(): number | null {
      const ours = Number(this.data.dsBenefit.CommissionRatioOurPortion);
      const advisor = Number(this.data.dsBenefit.CommissionRatioAdvisorPortion);
      if (Number.isNaN(ours) || Number.isNaN(advisor)) return null;
      return ours + advisor;
    }

    async autofillAdvisor() {
      const ours = Number(this.data.dsBenefit.CommissionRatioOurPortion);
      if (Number.isFinite(ours) && ours >= 0 && ours <= 100) {
        this.data.dsBenefit.CommissionRatioAdvisorPortion = 100 - ours;
      }
    }

    async autofillOurs() {
      const advisor = Number(this.data.dsBenefit.CommissionRatioAdvisorPortion);
      if (Number.isFinite(advisor) && advisor >= 0 && advisor <= 100) {
        this.data.dsBenefit.CommissionRatioOurPortion = 100 - advisor;
      }
    }

    footer({ field, draw, action }: RenderEvent) {
      const sum = this.splitSum();
      const isDisabled =
        this.data.dsBenefit.CommissionRatioOurPortion === null ||
        this.data.dsBenefit.CommissionRatioAdvisorPortion === null ||
        sum !== 100;
      return (
        <Dialog.Footer>
          <Button
            action={action(this.submit)}
            label="Save"
            size="large"
            style="primary"
            disable={isDisabled}
          />
        </Dialog.Footer>
      );
    }

    details({ field, draw, action }: RenderEvent) {
      const sum = this.splitSum();
      const showError =
        this.data.dsBenefit.CommissionRatioOurPortion !== null &&
        this.data.dsBenefit.CommissionRatioAdvisorPortion !== null &&
        sum !== 100;
      const defaults = this.state.defaults;
      return (
        <Card>
          {defaults && defaults.distros.length > 0 && (
            <div style={{ marginBottom: 12, fontSize: 13, color: "#333" }}>
              {defaults.distros.map((d) => (
                <div key={d.distroAccountId} style={{ marginBottom: 4 }}>
                  Default split{d.scenarios.length > 1 ? "s" : ""} for{" "}
                  <strong>{d.distroName}</strong>:{" "}
                  {d.scenarios
                    .map(
                      (s) =>
                        `${s.label ? s.label + " — " : ""}Our ${s.ourPortion}% / Advisor ${s.advisorPortion}%${
                          s.notes ? " (" + s.notes + ")" : ""
                        }`
                    )
                    .join("; ")}
                </div>
              ))}
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              marginBottom: 8,
              fontSize: 13,
              color: "#0b5394",
            }}
          >
            KTM common calculations
            <CustomToolTip
              header="KTM common calculations"
              bodyContent={<KtmCommonCalculationsBody />}
            />
          </div>
          <Line>
            <Display
              required
              field={field("CommissionRatioOurPortion")}
              onValueChanged={action(this.autofillAdvisor)}
              toolTip={
                <CustomToolTip
                  header="Our Portion"
                  bodyContent="Our Portion plus the Advisor portion must equal a total of 100. The other field auto-fills as you type."
                />
              }
            />
            <Display
              required
              field={field("CommissionRatioAdvisorPortion")}
              onValueChanged={action(this.autofillOurs)}
              toolTip={
                <CustomToolTip
                  header="Advisor Portion"
                  bodyContent="Our Portion plus the Advisor portion must equal a total of 100. The other field auto-fills as you type."
                />
              }
            />
          </Line>
          {showError && (
            <div
              style={{
                color: "#b00020",
                fontSize: 13,
                marginTop: 8,
              }}
            >
              Our Portion + Advisor Portion must equal 100 (currently {sum}).
            </div>
          )}
        </Card>
      );
    }

    main_tab({ field, draw, action }: RenderEvent) {
      return (
        <Panel>
          <Column>{draw(this.details)}</Column>
        </Panel>
      );
    }

    getChangedFields(originalData, updatedData) {
      if (!originalData) return {};

      const changes = {};
      for (const key in updatedData) {
        if (updatedData[key] !== originalData[key]) {
          changes[key] = {
            oldValue:
              originalData[key] !== undefined ? originalData[key] : "N/A",
            newValue: updatedData[key] !== undefined ? updatedData[key] : "N/A",
          };
        }
      }
      return changes;
    }

    async submit({ screen }: ActionEvent) {
      if (this.splitSum() !== 100) {
        toastr.error("Our Portion + Advisor Portion must equal 100.");
        return;
      }

      // Track changes
      const changes = this.getChangedFields(
        this.originalData,
        this.data.dsBenefit
      );

      const logDetails = Object.entries(changes)
        .map(([key, value]) => {
          const change = value as { oldValue: any; newValue: any };
          return `${key}: ${change.oldValue} → ${change.newValue}`;
        })
        .join(" | ");

      const policyLogs = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Note: logDetails,
          Action: "Policy Update Product commission ratio",
          PolicyId: this.props.policy_id,
        }),
      };
      await KTMApi.action(screen, "submit", {
        "@dsBenefit.CommissionRatioOurPortion":
          this.data.dsBenefit.CommissionRatioOurPortion,
        "@dsBenefit.CommissionRatioAdvisorPortion":
          this.data.dsBenefit.CommissionRatioAdvisorPortion,
        "@policyBenefit_id": this.props.policyBenefit_id,
      }).then(() => {
        fetch("/api/PlanAdmin/updatepolicylogs", policyLogs);
      });
      screen.close({ continue: true });
    }
  }
);
